/**
 * Hook: usePipelineCard
 *
 * Operações CRUD para cards em pipelines
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';
import { PipelineCard, CreateCardRequest, UpdateCardRequest, CardHistory } from '../types';

export function useCreateCard() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (request: CreateCardRequest) => {
      if (!profile?.uuid) throw new Error('Usuário não autenticado');
      return await createCard(request, profile.uuid);
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ['pipelineBoard', request.pipelineId] });
    }
  });
}

export function useUpdateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateCardRequest) => {
      return await updateCard(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineBoard'] });
    }
  });
}

export function useDeleteCard() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (cardId: string) => {
      if (!profile?.uuid) throw new Error('Usuário não autenticado');
      return await deleteCard(cardId, profile.uuid);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pipelineBoard'] });
    }
  });
}

export function useCardHistory(cardId: string) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['cardHistory', cardId],
    queryFn: async () => {
      return await fetchCardHistory(cardId);
    },
    enabled: !!cardId,
    staleTime: 1000 * 60 * 2
  });

  return {
    history: data || [],
    loading: isLoading,
    error: error as Error | null,
    refetch
  };
}

async function createCard(request: CreateCardRequest, userId: string): Promise<PipelineCard> {
  const {
    pipelineId,
    stageId,
    tenantId,
    responsavelId,
    dadosCard,
    clienteId,
    dataParaAgendamento,
    horaAgendamento,
    dataCriacao,
  } = request;

  const dadosComHora =
    horaAgendamento && !dadosCard.hora_agendamento
      ? { ...dadosCard, hora_agendamento: horaAgendamento.slice(0, 5) }
      : dadosCard;

  const { data, error } = await supabase
    .from('pipeline_cards')
    .insert({
      pipeline_id: pipelineId,
      stage_id: stageId,
      tenant_id: tenantId,
      cliente_id: clienteId ?? null,
      criado_por: userId,
      responsavel_id: responsavelId,
      dados_card: dadosComHora as any,
      data_para_agendamento: dataParaAgendamento ?? null,
      cancelado: false,
      ...(dataCriacao ? { data_criacao: dataCriacao } : {})
    })
    .select()
    .single();

  if (error) {
    throw new Error(`Erro ao criar card: ${error.message}`);
  }

  if (data) {
    await supabase.from('card_history').insert({
      card_id: data.id,
      usuario_id: userId,
      acao: 'criado',
      stage_novo_id: stageId
    });
  }

  return data as PipelineCard;
}

async function updateCard(request: UpdateCardRequest): Promise<PipelineCard> {
  const {
    cardId,
    dadosCard,
    dadosCardPatch,
    stageId,
    responsavelId,
    dataCriacao,
    dataParaAgendamento,
  } = request;

  const updatePayload: Record<string, unknown> = {
    data_ultima_atualizacao: new Date().toISOString()
  };

  if (dadosCard) updatePayload.dados_card = dadosCard as any;
  if (dadosCardPatch) {
    const { data: atual, error: fetchError } = await supabase
      .from('pipeline_cards')
      .select('dados_card')
      .eq('id', cardId)
      .single();
    if (fetchError) {
      throw new Error(`Erro ao carregar card: ${fetchError.message}`);
    }
    updatePayload.dados_card = {
      ...((atual?.dados_card as Record<string, unknown>) || {}),
      ...dadosCardPatch,
    } as any;
  }
  if (stageId) updatePayload.stage_id = stageId;
  if (responsavelId) updatePayload.responsavel_id = responsavelId;
  if (dataCriacao) updatePayload.data_criacao = dataCriacao;
  if (dataParaAgendamento) updatePayload.data_para_agendamento = dataParaAgendamento;

  const { data, error } = await supabase
    .from('pipeline_cards')
    .update(updatePayload)
    .eq('id', cardId)
    .select()
    .single();

  if (error) {
    throw new Error(`Erro ao atualizar card: ${error.message}`);
  }

  return data as PipelineCard;
}

async function deleteCard(cardId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('pipeline_cards')
    .update({
      cancelado: true,
      motivo_cancelamento: 'Deletado',
      data_ultima_atualizacao: new Date().toISOString()
    })
    .eq('id', cardId);

  if (error) {
    throw new Error(`Erro ao deletar card: ${error.message}`);
  }

  await supabase.from('card_history').insert({
    card_id: cardId,
    usuario_id: userId,
    acao: 'deletado'
  });
}

async function fetchCardHistory(cardId: string): Promise<CardHistory[]> {
  const { data, error } = await supabase
    .from('card_history')
    .select('*')
    .eq('card_id', cardId)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(`Erro ao buscar histórico: ${error.message}`);
  }

  return (data || []) as CardHistory[];
}
